import React from 'react';
import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Tooltip,
  Box,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import { useSelector } from 'react-redux';

import GpuClass from './GpuClass';

const cpu_gpu_type_id = '78201a90-ca48-4147-b6f5-fec51b4bd7db';

const GpuTypeField = (props) => {
  const gpu_classes_raw = useSelector((state) => state.gpuClasses || []);
  const gpu_classes = [...gpu_classes_raw].sort((a, b) =>
    a.id === cpu_gpu_type_id ? 1 : a.name > b.name ? 1 : -1
  );

  const disabled = props.edit && props.gpu_types[0] === cpu_gpu_type_id;

  if (props.cards) {
    const isLoading = !gpu_classes || gpu_classes.length === 0;

    return (
      <Box>
        <Typography variant='body1' component='label' sx={{ mb: 1, display: 'block' }}>
          Gpu Type <span style={{ color: 'red' }}>*</span>
        </Typography>
        <Grid container spacing={2}>
          {isLoading ? (
            // Show skeleton placeholders while loading
            (Array.from({ length: 8 }).map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={`skeleton-${index}`}>
                <Card
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent>
                    <Skeleton variant='text' width='60%' height={32} sx={{ mb: 1 }} />
                    <Skeleton variant='text' width='80%' height={24} sx={{ mb: 1 }} />
                    <Skeleton variant='text' width='70%' height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant='text' width='75%' height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant='text' width='70%' height={20} sx={{ mb: 0.5 }} />
                  </CardContent>
                  <CardContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Skeleton variant='circular' width={24} height={24} sx={{ mx: 'auto' }} />
                  </CardContent>
                </Card>
              </Grid>
            )))
          ) : (
            gpu_classes.map((gpu_class) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={gpu_class.id}>
                <GpuClass
                  details={gpu_class}
                  onClick={props.onClick}
                  selected={props.gpu_types.includes(gpu_class.id)}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Box>
    );
  } else {
    const options = gpu_classes.map((gpu_class) => ({
      id: gpu_class.id,
      label: gpu_class.name,
      details: gpu_class,
      disabled: gpu_class.id === cpu_gpu_type_id,
    }));

    const selected_values = props.gpu_types
      .map((gpu_type_id) => {
        const gpu_class = gpu_classes.find((gc) => gc.id === gpu_type_id);
        return gpu_class
          ? {
              id: gpu_class.id,
              label: gpu_class.name,
              details: gpu_class,
              disabled: gpu_class.id === cpu_gpu_type_id,
            }
          : null;
      })
      .filter((v) => v !== null);

    const handleChange = (event, new_value) => {
      if (Array.isArray(new_value)) {
        props.onClick(new_value.map((option) => option.id));
      } else {
        props.onClick(new_value ? [new_value.id] : []);
      }
    };

    return (
      <Autocomplete
        multiple
        id='gpu-type'
        options={options}
        value={selected_values}
        onChange={handleChange}
        disabled={disabled}
        getOptionDisabled={(option) => option.disabled}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.label || ''}
        renderOption={(props_option, option) => {
          const { details } = option;
          const tooltip_content = (
            <Box>
              <Typography variant='body2'>
                Credits Per Hour:{' '}
                {details.price.max === details.price.min
                  ? details.price.max
                  : `${details.price.min} - ${details.price.max}`}
              </Typography>
              <Typography variant='body2'>
                {details.performance.memory_size} GB GPU RAM
              </Typography>
              <Typography variant='body2'>
                {details.performance.fp32_rate} TFLOPS (fp32)
              </Typography>
              <Typography variant='body2'>
                {details.performance.fp16_rate} TFLOPS (fp16)
              </Typography>
            </Box>
          );

          return (
            <Tooltip title={tooltip_content} key={option.id}>
              <Box component='li' {...props_option}>
                {option.label}
              </Box>
            </Tooltip>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label='Gpu Type' />
        )}
      />
    );
  }
};

export default GpuTypeField;

